import React, { useState } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";

import EncodingNotifications from "./EncodingNotifications";
import FactorAuth from "./FactorAuth";
import PersonalInformationsForm from "./PersonalInformationsForm";
import PushNotificationSwitch from "./PushNotificationSwitch";
import { UPDATE_PROFILE } from "../../actions/ActionTypes";
import { logout } from "../../actions/LoginActions";
import { postApi } from "../../actions/SendActions";
import AdminUserAPI from "../../api/AdminUserAPI";
import { confirm2FA, disable2FA, setup2FA } from "../../api/legacy/ProfileAPI";
import ProjectAPI from "../../api/ProjectAPI";
import { OWNER } from "../../constants/roles";
import "../../css/admin/profile.scss";
import useAbility from "../../hooks/useAbility";
import { validatePasswordRules } from "../../utils/data/User";
import toBase64 from "../../utils/images";
import { LOGIN_ROUTE } from "../routes/routes";
import Breadcrumbs from "../utils/Breadcrumbs";
import ContainerHeader from "../utils/ContainerHeader";

import type { JSX } from "react";

import type {
  UpdateOrganizationParams,
  UpdateUserPreferencesParams,
} from "../../api/AdminUserAPI.types";
import type { AppState } from "../../reducers";

export interface PersonalInformationsFormValues {
  firstName: string;
  lastName: string;
  email: string;
  avatar: string | File;
  currentPassword: string;
  newPassword: string;
  mailNotifications: boolean;
  zapierNotifications?: boolean;
  inAppNotifications: boolean;
}

function ProfileAdmin(): JSX.Element {
  const dispatch = useDispatch();
  const { t } = useTranslation("admin-profile");

  const [confirmed, setConfirmed] = useState(false);
  const [scratchCodes, setScratchCodes] = useState<string[]>([]);
  const [secretQR, setSecretQR] = useState<string | null>(null);
  const orgaOptions = useSelector(
    (state: AppState) =>
      state.fetchReducer.orga_config.data?.organization_options,
  );
  const token = useSelector((state: AppState) => state.loginReducer.token);

  const has2fa = useSelector(
    (state: AppState) => state.fetchReducer.user.data.has_2fa,
  );

  const queryClient = useQueryClient();

  const { canAccess, hasRole } = useAbility();

  const isOwner = hasRole([OWNER]);

  const { project: canAccessProject } = canAccess(orgaOptions);

  const getOrganizationParamsQuery = ProjectAPI.generateQuery({
    fn: ProjectAPI.getOrganizationParams,
    params: [],
    options: {
      enabled: isOwner,
    },
  });

  const { data: organizationParams } = useQuery(getOrganizationParamsQuery);

  const getUserProfileQuery = AdminUserAPI.generateQuery({
    fn: AdminUserAPI.getUserProfile,
    params: [],
  });

  const { data: userProfile } = useQuery(getUserProfileQuery);

  const methods = useForm<PersonalInformationsFormValues>({
    values: {
      firstName: userProfile?.first_name ?? "",
      lastName: userProfile?.last_name ?? "",
      email: userProfile?.email ?? "",
      avatar: userProfile?.avatar_url ?? "",
      currentPassword: "",
      newPassword: "",
      mailNotifications:
        userProfile?.notification_settings?.notif_encoding_finished_mail ??
        false,
      zapierNotifications: isOwner
        ? (organizationParams?.notif_encoding_finished_zapier ?? false)
        : undefined,
      inAppNotifications:
        userProfile?.notification_settings?.notif_encoding_finished_bo ?? false,
    },
  });

  const onActivate = (resp: {
    scratch_codes: string[];
    secret_qr: string;
  }): void => {
    if (resp) {
      setScratchCodes([...resp.scratch_codes]);
      setSecretQR(resp.secret_qr);
    }
  };

  const onConfirm = (resp: { message: string }): void => {
    if (resp) setConfirmed(true);
  };

  const onDisable = (resp: { message: string }): void => {
    if (resp) {
      setScratchCodes([]);
      setSecretQR(null);
      setConfirmed(true);
    }
  };

  const showToastError = (errorMessage: string): void => {
    toast.error(errorMessage);
  };

  const handleChange2Fa = (label: string, value: boolean): void => {
    if (value && label === "activate" && !has2fa) {
      dispatch(setup2FA(token, onActivate, showToastError, t));

      setConfirmed(false);
    }
  };

  const handleSubmit2Fa = (data: { otp_token: string }): void => {
    if (!has2fa) {
      dispatch(confirm2FA(data, token, onConfirm, showToastError, t));
    } else {
      dispatch(disable2FA(data, token, onDisable, showToastError, t));
    }
  };

  const isValidatePassword = (password: string): boolean => {
    if (
      !validatePasswordRules(password, [
        "min8",
        "lowercase",
        "uppercase",
        "digits",
      ])
    ) {
      showToastError(t("profile:badPassword"));
      return false;
    }

    return true;
  };

  const updatePassword = useMutation({
    mutationFn: async (data: {
      currentPassword: string;
      newPassword: string;
    }) => AdminUserAPI.updateUserPassword(data),
    onSuccess: () => {
      toast.success(t("profile:passwordUpdated"));
    },
    onError: (e) => {
      showToastError(t("profile:badPassword"));
      console.error(e);
    },
  });

  const updateUserPreferences = useMutation({
    mutationFn: async (data: UpdateUserPreferencesParams) =>
      AdminUserAPI.updateUserPreferences(data),
    onSuccess: (data) => {
      toast.success(t("updateNotificationsSuccess"), {
        id: "update-notifications-success",
      });
      const updatedUserProfile = {
        ...userProfile,
        notification_settings: {
          notif_encoding_finished_mail: data.notif_encoding_finished_mail,
          notif_encoding_finished_bo: data.notif_encoding_finished_bo,
        },
      };

      queryClient.setQueryData(
        getUserProfileQuery.queryKey,
        updatedUserProfile,
      );
    },
    onError: (e) => {
      showToastError(t("genericError", { ns: "error" }));
      console.error(e);
    },
  });

  const updateOrganizationParams = useMutation({
    mutationFn: async (data: UpdateOrganizationParams) =>
      AdminUserAPI.updateOrganizationParams(data),
    onSuccess: (data) => {
      toast.success(t("updateNotificationsSuccess"), {
        id: "update-notifications-success",
      });
      const updatedOrganizationParams = {
        ...organizationParams,
        notif_encoding_finished_zapier: data.notif_encoding_finished_zapier,
      };

      queryClient.setQueryData(
        getOrganizationParamsQuery.queryKey,
        updatedOrganizationParams,
      );
    },
    onError: (e) => {
      showToastError(t("genericError", { ns: "error" }));
      console.error(e);
    },
  });

  const handleProfileSave = async (): Promise<void> => {
    const formValues = methods.getValues();

    if (
      formValues.firstName !== userProfile?.first_name ||
      formValues.lastName !== userProfile?.last_name ||
      formValues.email !== userProfile?.email ||
      formValues.avatar !== userProfile?.avatar_url
    ) {
      const avatarBase64 = await toBase64(formValues.avatar);
      dispatch(
        postApi({
          endpoint: "/api/user/v3/profile",
          type: UPDATE_PROFILE,
          data: {
            last_name: formValues.lastName,
            first_name: formValues.firstName,
            email: formValues.email,
            image: avatarBase64,
          },
          token,
          message: t("profile:personalInformationUpdated"),
        }),
      );
    }

    if (formValues.currentPassword || formValues.newPassword) {
      if (isValidatePassword(formValues.newPassword)) {
        updatePassword.mutate({
          currentPassword: formValues.currentPassword,
          newPassword: formValues.newPassword,
        });
      }
    }

    if (
      formValues.mailNotifications !==
        userProfile?.notification_settings?.notif_encoding_finished_mail ||
      formValues.inAppNotifications !==
        userProfile?.notification_settings?.notif_encoding_finished_bo
    ) {
      updateUserPreferences.mutate({
        encodingNotifications: {
          email: formValues.mailNotifications,
          inApp: formValues.inAppNotifications,
        },
      });
    }

    if (
      isOwner &&
      formValues.zapierNotifications !== undefined &&
      formValues.zapierNotifications !==
        organizationParams?.notif_encoding_finished_zapier
    ) {
      updateOrganizationParams.mutate({
        encodingNotifications: {
          zapier: formValues.zapierNotifications,
        },
      });
    }
  };

  const breadcrumbs = [
    {
      name: t("editor-menu:profile"),
      url: "/profile",
    },
  ];

  return (
    <div className="editor-container editor-header-simple editor-has-breadcrumbs">
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <FormProvider {...methods}>
        <Breadcrumbs items={breadcrumbs} />
        <ContainerHeader
          onSave={handleProfileSave}
          item={{
            label: t("personalInformation"),
          }}
        />
        <div className="flex flex-col">
          <PersonalInformationsForm />
          <div className="editor-tile-container">
            <div className="editor-form-section">
              {canAccessProject.organization ? (
                <PushNotificationSwitch />
              ) : null}
              <FactorAuth
                auth={{ scratchCodes, secretQR, confirmed }}
                onChange={handleChange2Fa}
                onSubmit={handleSubmit2Fa}
                onLogout={(): void =>
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
                  dispatch(logout(token, null, LOGIN_ROUTE))
                }
              />
              <EncodingNotifications />
            </div>
          </div>
        </div>
      </FormProvider>
    </div>
  );
}

export default ProfileAdmin;
